import { useEffect, useRef, useState } from "react";
import {
  Sparkles,
  Send,
  Mic,
  Paperclip,
  AlertTriangle,
  Loader2,
  Wand2,
  TrendingUp,
  KeyRound,
  Layers,
  Activity,
  CheckCircle2,
  RefreshCw,
  ChevronDown,
  Search,
  Check,
  Target,
  Plus,
  Zap,
  Trash2,
  Megaphone,
  Link2,
  X,
} from "lucide-react";
import { glass, BURNT, BURNT_GLOW, BLUE, GREEN, ORANGE, AriaTrigger } from "./shared";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useOwnClientId } from "@/hooks/useOwnClientId";
import {
  normalizeWorkspace, normalizeAdGroups, validateAd, validateAdGroup, validateSitelink, scopeTotals,
  headlineTooLong, descTooLong, sitelinkTextTooLong, HEADLINE_MAX, DESC_MAX, SITELINK_TEXT_MAX,
  pickReplacementSitelink,
  type AdGroupSpec, type SiteInventory,
} from "./adWorkspace";

function extractDomain(url?: string | null): string | null {
  if (!url) return null;
  try {
    const u = new URL(url.startsWith("http") ? url : `https://${url}`);
    return u.hostname.replace(/^www\./, "");
  } catch {
    return null;
  }
}

// Recently-worked-on campaigns, per client, most-recent-first. Persisted so the
// list survives across sessions. Store up to 8; the menu shows the top 5.
const RECENT_MAX = 8;
const recentKey = (clientId: string) => `aria:recent-campaigns:${clientId}`;
function loadRecentIds(clientId: string | null): string[] {
  if (!clientId) return [];
  try {
    const arr = JSON.parse(localStorage.getItem(recentKey(clientId)) || "[]");
    return Array.isArray(arr) ? arr.map(String) : [];
  } catch { return []; }
}
function saveRecentIds(clientId: string | null, ids: string[]) {
  if (!clientId) return;
  try { localStorage.setItem(recentKey(clientId), JSON.stringify(ids.slice(0, RECENT_MAX))); } catch { /* ignore */ }
}

type Mode = "standard" | "advanced";
type Msg = { id: string; role: "user" | "assistant"; content: string };

const SUGGESTED = [
  { icon: TrendingUp, label: "Improve my CTR" },
  { icon: Activity, label: "Reduce wasted spend" },
  { icon: Layers, label: "Build new ad group" },
  { icon: KeyRound, label: "Find high-converting keywords" },
  { icon: Wand2, label: "Analyze campaign performance" },
];

const AriaTab = ({ trigger }: { trigger?: AriaTrigger }) => {
  const [mode, setMode] = useState<Mode>("standard");
  const [showWarning, setShowWarning] = useState(false);
  const [messages, setMessages] = useState<Msg[]>([
    {
      id: "w",
      role: "assistant",
      content:
        "Hey — I'm **ARIA**, your AI Google Ads expert. I've got full context on your account. Ask me anything, or pick a suggestion below.",
    },
  ]);
  const [input, setInput] = useState("");
  const [thinking, setThinking] = useState(false);
  const [workspace, setWorkspace] = useState<null | {
    type: "ad" | "keywords" | "sitelinks" | "budget" | "intent" | "image";
    title: string;
    payload: any;
  }>(null);
  const [adRegenCount, setAdRegenCount] = useState(0);
  const lastAdPromptRef = useRef<string | null>(null);
  const [isRecording, setIsRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const recordingBaseRef = useRef<string>("");
  const [adImages, setAdImages] = useState<{ id: string; url: string; name: string; generated?: boolean }[]>([]);
  const [dragOver, setDragOver] = useState(false);
  const [generatingImage, setGeneratingImage] = useState(false);
  const generatedImageIdRef = useRef<string | null>(null);
  const imageInputRef = useRef<HTMLInputElement | null>(null);

  const generateAdImage = async () => {
    if (generatingImage) return;
    const headline =
      workspace?.type === "ad"
        ? (normalizeAdGroups(workspace.payload)[0]?.ads?.[0]?.headlines?.[0] || "")
        : "";
    const keyword = lastAdPromptRef.current || "";
    const basis = [headline, keyword].filter(Boolean).join(" — ");
    if (!basis) {
      toast.error("Build an ad first so I know what to generate.");
      return;
    }
    const prompt = `Generate a professional commercial advertising photo for this ad. Headline: "${headline || basis}". Target keyword: "${keyword}". Show a real professional of this business actively doing their job for a real customer.`;
    setGeneratingImage(true);
    try {
      const { data, error } = await supabase.functions.invoke("dalle-generate", {
        body: {
          prompt,
          business: {
            domain: activeDomain,
            headline,
            keyword,
          },
        },
      });
      if (error) throw error;
      const url = data?.dataUrl;
      if (!url) throw new Error("No image returned");
      const newId = crypto.randomUUID();
      setAdImages((prev) => {
        const filtered = prev.filter((p) => p.id !== generatedImageIdRef.current);
        return [...filtered, { id: newId, url, name: "AI generated", generated: true }];
      });
      generatedImageIdRef.current = newId;
    } catch (err: any) {
      console.error("Image generate error:", err);
      toast.error("Couldn't generate image. Please try again.");
    } finally {
      setGeneratingImage(false);
    }
  };

  const handleImageFiles = (files: FileList | File[]) => {
    const accepted = ["image/jpeg", "image/jpg", "image/png"];
    const next: { id: string; url: string; name: string }[] = [];
    Array.from(files).forEach((f) => {
      if (!accepted.includes(f.type)) {
        toast.error(`${f.name}: only JPG and PNG are supported`);
        return;
      }
      if (f.size > 5 * 1024 * 1024) {
        toast.error(`${f.name}: max 5MB`);
        return;
      }
      next.push({ id: crypto.randomUUID(), url: URL.createObjectURL(f), name: f.name });
    });
    if (next.length) setAdImages((prev) => [...prev, ...next]);
  };

  const removeAdImage = (id: string) => {
    setAdImages((prev) => {
      const found = prev.find((p) => p.id === id);
      if (found && !found.generated) URL.revokeObjectURL(found.url);
      return prev.filter((p) => p.id !== id);
    });
    if (generatedImageIdRef.current === id) generatedImageIdRef.current = null;
  };

  useEffect(() => {
    return () => {
      adImages.forEach((i) => {
        if (!i.generated) URL.revokeObjectURL(i.url);
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleRecording = async () => {
    if (isRecording) {
      mediaRecorderRef.current?.stop();
      return;
    }
    if (!navigator.mediaDevices?.getUserMedia) {
      toast.error("Audio recording isn't supported in this browser.");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "";
      const recorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
      audioChunksRef.current = [];
      recordingBaseRef.current = input;

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data);
      };
      recorder.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        setIsRecording(false);
        mediaRecorderRef.current = null;
        const blob = new Blob(audioChunksRef.current, { type: recorder.mimeType || "audio/webm" });
        if (blob.size === 0) return;
        setTranscribing(true);
        try {
          const ext = (recorder.mimeType || "audio/webm").includes("mp4") ? "mp4" : "webm";
          const file = new File([blob], `recording.${ext}`, { type: blob.type });
          const form = new FormData();
          form.append("file", file);
          const { data, error } = await supabase.functions.invoke("whisper-transcribe", {
            body: form,
          });
          if (error) throw error;
          const text = (data?.text || "").trim();
          if (!text) {
            toast.error("Couldn't understand the audio. Please try again.");
            return;
          }
          const base = recordingBaseRef.current;
          const sep = base && !/\s$/.test(base) ? " " : "";
          setInput(base + sep + text);
        } catch (err: any) {
          console.error("Whisper transcribe error:", err);
          toast.error("Transcription failed. Please try again.");
        } finally {
          setTranscribing(false);
        }
      };
      mediaRecorderRef.current = recorder;
      setIsRecording(true);
      recorder.start();
    } catch (err: any) {
      console.error("Mic access error:", err);
      if (err?.name === "NotAllowedError" || err?.name === "SecurityError") {
        toast.error("Microphone access was denied. Please allow mic permissions in your browser settings.");
      } else if (err?.name === "NotFoundError") {
        toast.error("No microphone was found. Please connect a mic and try again.");
      } else {
        toast.error("Couldn't start recording. Please try again.");
      }
    }
  };
  const MAX_REGEN = 3;
  const scrollRef = useRef<HTMLDivElement>(null);
  const { ownClientId } = useOwnClientId("AriaTab");
  const [accountCtx, setAccountCtx] = useState<{
    domain: string | null;
    campaigns: { id?: string; name: string; domain?: string | null; adDomain?: string | null; overrideDomain?: string | null; adPages?: string[]; strat?: string | null; biddingStrategyType?: string | null }[];
  }>({ domain: null, campaigns: [] });

  // VERIFIED PAGE INVENTORY for the active domain (sitemap-derived, cached). The ONLY source
  // of real sitelink URLs — gates every sitelink so none can be invented (→ never 404).
  const [siteInventory, setSiteInventory] = useState<SiteInventory | null>(null);
  const inventoryFetchedRef = useRef<string | null>(null); // domain currently loaded/loading

  // ACTIVE CAMPAIGN — the toggle in the top bar is the single source of truth for
  // which campaign Aria works on. Picking one locks it here + drives her targeting.
  const [activeCampaign, setActiveCampaign] = useState<{ id: string; name: string } | null>(null);
  const [recentIds, setRecentIds] = useState<string[]>([]);
  const [selectorOpen, setSelectorOpen] = useState(false);
  const [campaignQuery, setCampaignQuery] = useState("");
  const [domainDraft, setDomainDraft] = useState(""); // ② per-campaign domain override input

  // BUILD ASSEMBLER (Advanced mode) — a client-only build plan the user assembles with
  // structured actions, then one Generate composes a brief and reuses handleSend, so the
  // result flows through the EXISTING aria-chat → workspace preview → confirm gate → push.
  type PlanKeyword = { text: string; match: "Exact" | "Phrase" | "Broad" };
  type PlanGroup = { id: string; name: string; keywords: PlanKeyword[]; ads: number };
  const [plan, setPlan] = useState<{ groups: PlanGroup[]; sitelinks: boolean }>({ groups: [], sitelinks: true });
  const [activeGroupId, setActiveGroupId] = useState<string | null>(null);
  const [assemblerOpen, setAssemblerOpen] = useState(true);
  const [draftMode, setDraftMode] = useState<null | "group" | "keyword">(null);
  const [draftText, setDraftText] = useState("");
  const [draftMatch, setDraftMatch] = useState<"Exact" | "Phrase" | "Broad">("Phrase");

  // Established target campaign + operation for THIS conversation. Persists
  // across follow-up content corrections so fixing a keyword never resets the
  // target campaign. Updated only when the user explicitly changes the target.
  const [sessionAction, setSessionAction] = useState<
    null | { op: string; targetCampaignId: string | null; targetCampaignName: string | null }
  >(null);
  // The action attached to the CURRENT workspace (what a write would actually do).
  const [pendingAction, setPendingAction] = useState<
    null | { op: string; targetCampaignId: string | null; targetCampaignName: string | null }
  >(null);

  // Persist the established target across turns. A content-only correction
  // (add_* that echoes or omits the target) keeps the existing targetCampaignId.
  const mergeSession = (a: any) => {
    if (!a || typeof a.op !== "string") return;
    setSessionAction((prev) => {
      if (a.op === "create_campaign") return { op: "create_campaign", targetCampaignId: null, targetCampaignName: null };
      if (a.targetCampaignId) return { op: a.op, targetCampaignId: String(a.targetCampaignId), targetCampaignName: a.targetCampaignName ?? prev?.targetCampaignName ?? null };
      if (prev?.targetCampaignId) return { op: a.op, targetCampaignId: prev.targetCampaignId, targetCampaignName: prev.targetCampaignName };
      return prev;
    });
  };

  // Load client's domain + campaign list for ad URL grounding
  useEffect(() => {
    if (!ownClientId) return;
    let cancelled = false;
    (async () => {
      try {
        const { data: clientRow } = await supabase
          .from("clients")
          .select("website_url")
          .eq("id", ownClientId)
          .maybeSingle();
        const domain = extractDomain((clientRow as any)?.website_url);

        let campaigns: { id?: string; name: string; domain?: string | null; adDomain?: string | null; overrideDomain?: string | null; adPages?: string[]; strat?: string | null; biddingStrategyType?: string | null }[] = [];
        try {
          const { data } = await supabase.functions.invoke("get-google-ads-campaigns", {
            body: { clientId: ownClientId, dateRange: "LAST_30_DAYS" },
          });
          if (data?.ok && Array.isArray(data.campaigns)) {
            campaigns = data.campaigns.map((c: any) => ({ id: c.id, name: c.name, domain: c.domain ?? null, adDomain: c.adDomain ?? null, overrideDomain: c.overrideDomain ?? null, adPages: Array.isArray(c.adPages) ? c.adPages : [], strat: c.strat ?? null, biddingStrategyType: c.biddingStrategyType ?? null }));
          }
        } catch (e) {
          console.warn("[AriaTab] campaign fetch failed", e);
        }
        if (!cancelled) setAccountCtx({ domain, campaigns });
      } catch (e) {
        console.warn("[AriaTab] account ctx failed", e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [ownClientId]);

  // Initialize the active campaign + recent list once campaigns are loaded:
  // first persisted-recent that still exists, else the first campaign.
  useEffect(() => {
    const campaigns = accountCtx.campaigns.filter((c) => c.id);
    if (!ownClientId || campaigns.length === 0) return;
    const recent = loadRecentIds(ownClientId);
    setRecentIds(recent);
    setActiveCampaign((prev) => {
      if (prev && campaigns.some((c) => String(c.id) === prev.id)) return prev;
      const firstRecent = recent.map((id) => campaigns.find((c) => String(c.id) === id)).find(Boolean);
      const pick = firstRecent || campaigns[0];
      return pick?.id ? { id: String(pick.id), name: pick.name } : null;
    });
  }, [accountCtx.campaigns, ownClientId]);

  // The active campaign drives the write target: mirror it into sessionAction so
  // buildOp / the confirm gate / the push all target it (not chat).
  useEffect(() => {
    if (activeCampaign) {
      setSessionAction({ op: "add_ad_group", targetCampaignId: activeCampaign.id, targetCampaignName: activeCampaign.name });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCampaign?.id]);

  // Pick a campaign from the toggle → it becomes active, jumps to the top of the
  // recent list, and Aria registers the switch with a short confirm.
  const pickCampaign = (c: { id?: string; name: string }) => {
    if (!c.id) return;
    const id = String(c.id);
    setActiveCampaign({ id, name: c.name });
    setRecentIds((prev) => {
      const next = [id, ...prev.filter((x) => x !== id)].slice(0, RECENT_MAX);
      saveRecentIds(ownClientId, next);
      return next;
    });
    setSelectorOpen(false);
    setCampaignQuery("");
    setMessages((m) => [
      ...m,
      { id: crypto.randomUUID(), role: "assistant", content: `Now working on the **${c.name}** campaign — what would you like to do?` },
    ]);
  };

  // ── PER-CAMPAIGN AD DOMAIN ── Resolution order for the domain Aria uses:
  //   1. UI override for the active campaign  →  2. dominant domain read from the
  //   campaign's existing ads  →  3. account/token-holder domain (last resort).
  // (override & ads are merged server-side into campaign.domain; account is the fallback.)
  const activeCampaignRow = activeCampaign ? accountCtx.campaigns.find((c) => String(c.id) === activeCampaign.id) : null;
  const activeDomain: string | null = ((activeCampaignRow?.domain as string | undefined) || accountCtx.domain || null);

  // BIDDING — the active campaign's REAL strategy (from the Ads API) decides how bids are
  // handled. Manual → the user enters the max-CPC bid per ad group (below); nothing is ever
  // invented. Impression-share / automated → no ad-group bid is set (campaign governs it).
  const activeBiddingType = String(activeCampaignRow?.biddingStrategyType || "");
  const isManualBidding = activeBiddingType === "MANUAL_CPC";
  const isImpressionShareBidding = activeBiddingType === "TARGET_IMPRESSION_SHARE";
  // User-entered max-CPC bid per ad-group INDEX (as typed, e.g. "1.25"); manual only.
  const [bids, setBids] = useState<Record<number, string>>({});
  // Reset entered bids whenever the active campaign changes (a new strategy/context).
  useEffect(() => { setBids({}); }, [activeCampaign?.id]);
  // The exact bid in MICROS for an ad group, or null if not a valid positive number.
  const bidMicros = (gi: number): string | null => {
    const n = parseFloat(String(bids[gi] ?? "").trim());
    return Number.isFinite(n) && n > 0 ? String(Math.round(n * 1_000_000)) : null;
  };

  // Load (and cache in state) the verified page inventory for a domain. Sitelink generation
  // awaits this so URLs can only ever be REAL pages (never invented). Falls back to the
  // campaign's proven ad URLs when a site has no usable sitemap.
  const loadInventory = async (domain: string | null): Promise<SiteInventory | null> => {
    if (!domain) return null;
    const base = domain.replace(/^www\./, "");
    if (siteInventory && siteInventory.domain === base) return siteInventory;
    try {
      const fallbackUrls = activeCampaignRow?.adPages || [];
      const { data } = await supabase.functions.invoke("get-site-pages", { body: { domain: base, fallbackUrls } });
      const inv = (data?.inventory || null) as SiteInventory | null;
      setSiteInventory(inv);
      inventoryFetchedRef.current = base;
      return inv;
    } catch (e) {
      console.warn("[AriaTab] inventory load failed", e);
      return null;
    }
  };

  // Compact inventory passed to the model: real service pages (label+url) it may SELECT from,
  // plus location names. The code-side gate enforces membership against the FULL inventory, so
  // this trimmed view can never let an invented URL through.
  const inventoryForModel = (inv: SiteInventory | null) => {
    if (!inv) return null;
    const baseSvc = (inv.services || []).filter((s) => !s.location);
    const svc = (baseSvc.length ? baseSvc : (inv.services || [])).slice(0, 50).map((s) => ({ label: s.label, url: s.url }));
    return {
      domain: inv.domain,
      homepage: inv.homepage?.url || null,
      contact: inv.contact?.url || null,
      services: svc,
      locations: (inv.locations || []).slice(0, 80).map((l) => l.label),
      source: inv.source,
    };
  };

  // Keep the override input in sync with the active campaign's current override.
  useEffect(() => {
    setDomainDraft((activeCampaignRow?.overrideDomain as string | undefined) || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCampaign?.id]);

  const saveDomainOverride = async (raw: string) => {
    if (!activeCampaign) return;
    const id = activeCampaign.id;
    const domain = extractDomain(raw.trim()) || "";
    // Optimistic local update — override wins over the ad-read domain.
    setAccountCtx((prev) => ({
      ...prev,
      campaigns: prev.campaigns.map((c) => String(c.id) === id
        ? { ...c, overrideDomain: domain || null, domain: domain || (c.adDomain ?? null) }
        : c),
    }));
    try {
      if (domain) {
        await supabase.from("campaign_domains").upsert({ campaign_id: id, domain, updated_at: new Date().toISOString() });
        toast.success(`Ad domain for “${activeCampaign.name}” set to ${domain}`);
      } else {
        await supabase.from("campaign_domains").delete().eq("campaign_id", id);
        toast.success("Override cleared — using the campaign's own ad domain.");
      }
    } catch {
      toast.error("Couldn't save the domain override.");
    }
  };

  // ── BUILD ASSEMBLER handlers ──
  const activeGroup = plan.groups.find((g) => g.id === activeGroupId) || null;

  const startAddGroup = () => { setDraftMode("group"); setDraftText(""); };
  const commitGroup = () => {
    const name = draftText.trim();
    if (!name) { setDraftMode(null); return; }
    const id = crypto.randomUUID();
    setPlan((p) => ({ ...p, groups: [...p.groups, { id, name, keywords: [], ads: 1 }] }));
    setActiveGroupId(id);
    setDraftMode(null); setDraftText("");
  };
  const startAddKeywords = () => {
    if (!activeGroupId) { toast.error("Add an ad group first, then add its keywords."); return; }
    setDraftMode("keyword"); setDraftText("");
  };
  const commitKeywords = () => {
    const raw = draftText.trim();
    if (!raw || !activeGroupId) { if (!raw) setDraftMode(null); return; }
    const kws = raw.split(",").map((s) => s.trim()).filter(Boolean).flatMap((t) => [
      { text: t, match: "Exact" as const },
      { text: t, match: "Phrase" as const }
    ]);
    setPlan((p) => ({ ...p, groups: p.groups.map((g) => g.id === activeGroupId ? { ...g, keywords: [...g.keywords, ...kws] } : g) }));
    setDraftText("");
  };
  const cycleAds = (id: string) => {
    setPlan((p) => ({ ...p, groups: p.groups.map((g) => g.id === id ? { ...g, ads: g.ads >= 3 ? 1 : g.ads + 1 } : g) }));
  };
  const bumpActiveAds = () => {
    if (!activeGroupId) { toast.error("Add an ad group first, then set how many ads."); return; }
    cycleAds(activeGroupId);
  };
  const removeGroup = (id: string) => {
    setPlan((p) => ({ ...p, groups: p.groups.filter((g) => g.id !== id) }));
    if (activeGroupId === id) setActiveGroupId(null);
  };
  const removeKeyword = (gid: string, idx: number) => {
    setPlan((p) => ({ ...p, groups: p.groups.map((g) => g.id === gid ? { ...g, keywords: g.keywords.filter((_, i) => i !== idx) } : g) }));
  };
  const clearPlan = () => { setPlan({ groups: [], sitelinks: true }); setActiveGroupId(null); setDraftMode(null); setDraftText(""); };

  // Compose a natural-language brief from the plan and reuse the EXISTING send path —
  // aria-chat generates every ad group, its RSAs + keywords, and the sitelinks; the
  // result flows into the existing Live Workspace preview and confirmation gate.
  const generateFromPlan = () => {
    if (thinking) return;
    if (!plan.groups.length) { toast.error("Add at least one ad group to build."); return; }
    const c = activeCampaign?.name || "the selected campaign";
    const lines: string[] = [
      `For the ${c} campaign, build the following. Generate complete responsive search ads with full headlines and descriptions within Google's character limits:`,
    ];
    plan.groups.forEach((g, i) => {
      const kw = g.keywords.length
        ? ` Keywords: ${g.keywords.map((k) => `${k.text} [${k.match}]`).join(", ")}.`
        : "";
      lines.push(`Ad group ${i + 1}: "${g.name}" with ${g.ads} responsive search ad${g.ads === 1 ? "" : "s"}.${kw}`);
    });
    if (plan.sitelinks) lines.push(`For EACH ad group also add 4 sitelinks: 2 kind:"specific" (sub-angles of that ad group's own service) + 2 kind:"main" (OTHER services the business offers, not this ad group's; "Contact Us" is the only allowed non-service). Every sitelink is a real service — never About Us or info pages. Each links to its own page.`);
    handleSend(lines.join("\n"), undefined, "advanced");
    setAssemblerOpen(false); // fold away so the Live Workspace is the focus; the plan is kept for tweak + re-generate
  };

  // Auto-scroll
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: 9e9, behavior: "smooth" });
  }, [messages, thinking]);

  // Handle external trigger (from Search Terms / Insights)
  useEffect(() => {
    if (!trigger) return;
    const forceAdv = !!trigger.forceAdvanced;
    if (forceAdv) setMode("advanced");
    handleSend(trigger.prompt, trigger.context, forceAdv ? "advanced" : undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trigger]);

  const requestMode = (m: Mode) => {
    if (m === "advanced") setShowWarning(true);
    else {
      setMode("standard");
      setWorkspace(null);
    }
  };

  // Demo tour: external control to switch mode (auto-confirms advanced)
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail === "advanced") {
        setShowWarning(false);
        setMode("advanced");
      } else if (detail === "standard") {
        setMode("standard");
        setWorkspace(null);
      }
    };
    window.addEventListener("orbit:aria-set-mode", handler);
    return () => window.removeEventListener("orbit:aria-set-mode", handler);
  }, []);

  const handleSend = async (raw: string, ctx?: Record<string, any>, forceMode?: Mode, isRegenerate = false) => {
    const t = raw.trim();
    if (!t) return;
    const userMsg: Msg = { id: crypto.randomUUID(), role: "user", content: t };
    const nextMessages = [...messages, userMsg];
    setMessages(nextMessages);
    setInput("");
    setThinking(true);
    const effMode: Mode = forceMode || mode;

    try {
      const history = nextMessages
        .filter((m) => m.id !== "w")
        .map((m) => ({ role: m.role, content: m.content }));

      // Verified page inventory for the active site — Aria SELECTS sitelink URLs from this;
      // she never builds them. Loaded (cached) before generation; the gate enforces it.
      const inv = effMode === "advanced" ? await loadInventory(activeDomain) : siteInventory;

      const mergedCtx = {
        ...(ctx || {}),
        account: {
          // The ACTIVE campaign's resolved domain (override → ads → account), so Aria
          // builds ad + sitelink URLs on the campaign's own site, not the account's.
          domain: activeDomain,
          campaigns: accountCtx.campaigns.map(({ adPages, ...c }) => c),
          campaignCount: accountCtx.campaigns.length,
          // Real pages Aria may use for sitelinks (the ONLY allowed URL set).
          inventory: inventoryForModel(inv),
        },
        // The campaign selected in the top-bar toggle — the SINGLE source of truth
        // for which campaign Aria works on. She targets this, never parses chat.
        activeCampaign,
        // Mirrors the toggle; anchors the target so a content correction never drops it.
        sessionAction,
      };

      const { data, error } = await supabase.functions.invoke("aria-chat", {
        body: { messages: history, mode: effMode, context: mergedCtx },
      });

      if (error) throw error;
      const reply = data?.reply || "Sorry — I couldn't generate a response. Try again?";
      setMessages((m) => [...m, { id: crypto.randomUUID(), role: "assistant", content: reply }]);
      // Capture the structured action + persist the established target/op.
      if (effMode === "advanced" && data?.action) {
        setPendingAction(data.action);
        mergeSession(data.action);
      } else if (effMode === "advanced" && !data?.workspace) {
        // A pure question turn — leave the established session target intact.
      }
      if (effMode === "advanced" && data?.workspace) {
        const ws = data.workspace;
        if (ws.type === "image") {
          const prompt: string =
            ws.payload?.prompt ||
            `High-quality photorealistic Google Ads marketing image for: ${t}. Clean composition, vibrant lighting, no text or logos, square format.`;
          // If no ad workspace exists, create a minimal image workspace so the right panel shows it.
          setWorkspace((prev) =>
            prev && prev.type === "ad"
              ? prev
              : { type: "image", title: ws.title || "AI image", payload: { prompt } },
          );
          (async () => {
            setGeneratingImage(true);
            try {
              const { data: imgData, error: imgErr } = await supabase.functions.invoke(
                "dalle-generate",
                {
                  body: {
                    prompt,
                    business: {
                      domain: activeDomain,
                      headline:
                        workspace?.type === "ad"
                          ? (normalizeAdGroups(workspace.payload)[0]?.ads?.[0]?.headlines?.[0] || null)
                          : null,
                      keyword: t,
                    },
                  },
                },
              );
              if (imgErr) throw imgErr;
              const url = imgData?.dataUrl;
              if (!url) throw new Error("No image returned");
              const newId = crypto.randomUUID();
              setAdImages((prev) => {
                const filtered = prev.filter((p) => p.id !== generatedImageIdRef.current);
                return [...filtered, { id: newId, url, name: "AI generated", generated: true }];
              });
              generatedImageIdRef.current = newId;
            } catch (err) {
              console.error("Aria image generation error:", err);
              toast.error("Couldn't generate that image. Please try again.");
            } finally {
              setGeneratingImage(false);
            }
          })();
        } else {
          let processedWorkspace = normalizeWorkspace(ws, inv);

          // Special handling for sitelink regeneration: merge new sitelinks into existing ad groups.
          // The AI is instructed to keep existing ads/keywords, but if its response is sparse,
          // we merge the new sitelinks into the current workspace, preserving other elements.
          if (isRegenerate && ws.type === "ad" && workspace?.type === "ad" && processedWorkspace?.type === "ad") {
            const currentAdGroups = normalizeAdGroups(workspace.payload, siteInventory);
            const newAdGroupsFromAI = normalizeAdGroups(ws.payload, inv); // Ad groups from the AI's response

            if (currentAdGroups.length > 0) {
                let mergedAdGroups = [...currentAdGroups];

                if (newAdGroupsFromAI.length > 0) {
                    // Case 1: AI returned ad groups with sitelinks (and possibly empty ads/keywords)
                    mergedAdGroups = currentAdGroups.map((currentGroup, index) => {
                        const aiGroup = newAdGroupsFromAI[index];
                        if (aiGroup && aiGroup.sitelinks.length > 0) {
                            return { ...currentGroup, sitelinks: aiGroup.sitelinks };
                        }
                        return currentGroup; // Preserve existing sitelinks if AI didn't provide new ones for this group
                    });
                } else if (ws.payload?.sitelinks?.length > 0) {
                    // Case 2: AI returned a flat list of sitelinks in ws.payload
                    const flatNewSitelinks = asArr(ws.payload.sitelinks).map((s) => normSitelink(s, inv)).filter((s) => s.text && s.url);
                    if (flatNewSitelinks.length > 0) {
                        // Apply these flat sitelinks to all existing ad groups.
                        mergedAdGroups = currentAdGroups.map(currentGroup => ({
                            ...currentGroup,
                            sitelinks: flatNewSitelinks
                        }));
                    }
                }
                processedWorkspace = { ...workspace, payload: { adGroups: mergedAdGroups } };
            }
          }

          setWorkspace(processedWorkspace);
          if (ws.type === "ad") {
            if (isRegenerate) {
              setAdRegenCount((c) => c + 1);
            } else {
              lastAdPromptRef.current = t;
              setAdRegenCount(0);
            }
          }
        }
      }
    } catch (err: any) {
      console.error("Aria chat error:", err);
      toast.error("ARIA couldn't reach the AI service");
      setMessages((m) => [
        ...m,
        {
          id: crypto.randomUUID(),
          role: "assistant",
          content: "Hmm, I hit a snag connecting to the AI service. Mind trying that again?",
        },
      ]);
    } finally {
      setThinking(false);
    }
  };

  const [publishing, setPublishing] = useState(false);
  // The confirmation gate: what a publish WOULD do. Set when the user clicks
  // Approve; a real write only happens when they Confirm this. Nothing writes
  // to Google Ads while this is null-or-pending.
  const [confirmOp, setConfirmOp] = useState<null | {
    op: string; targetCampaignId: string | null; targetCampaignName: string | null;
    isAdd: boolean;
    adGroups: AdGroupSpec[];
    totals: { adGroups: number; ads: number; keywords: number; sitelinks: number; issues: number };
  }>(null);

  // Resolve EXACTLY what a publish would do, from the established action + workspace.
  // Full scope: every ad group with its ads + keywords + per-ad-group sitelinks.
  const buildOp = () => {
    if (!workspace) return null;
    const a = pendingAction || sessionAction;
    const wantsCreate = a?.op === "create_campaign";
    // The ACTIVE CAMPAIGN (toggle) is the write target — unless the user explicitly
    // asked to create a brand-new campaign this turn. Falls back to the tracked
    // sessionAction target only when nothing is selected.
    const isAdd = !wantsCreate && !!(activeCampaign || a?.targetCampaignId);
    const op = wantsCreate ? "create_campaign" : (a?.op && a.op !== "create_campaign" ? a.op : "add_ad_group");
    const adGroups = normalizeAdGroups(workspace.payload, siteInventory);
    return {
      op,
      targetCampaignId: isAdd ? (activeCampaign?.id || String(a!.targetCampaignId)) : null,
      targetCampaignName: isAdd ? (activeCampaign?.name || a?.targetCampaignName || null) : null,
      isAdd,
      adGroups,
      totals: scopeTotals(adGroups),
    };
  };

  // Step 1 — user clicks Approve: OPEN the confirmation gate. Nothing is written here.
  const requestPublish = () => {
    if (publishing || !workspace) return;
    if (!ownClientId) { toast.error("Client not loaded yet — try again in a moment."); return; }
    const op = buildOp();
    if (!op) return;
    const hasAnyAd = op.adGroups.some((g) => (g.ads?.length ?? 0) > 0);
    const hasAnyKeyword = op.adGroups.some((g) => (g.keywords?.length ?? 0) > 0);
    const hasAnySitelink = op.adGroups.some((g) => (g.sitelinks?.length ?? 0) > 0);
    if (!hasAnyAd && !hasAnyKeyword && !hasAnySitelink) {
      toast.error("Nothing to publish yet — generate an ad, keywords, or sitelinks first.");
      return;
    }
    // MANUAL bidding → a bid must be entered for EVERY ad group. Ask, never invent.
    if (isManualBidding) {
      const missing = op.adGroups.map((_, gi) => gi).filter((gi) => bidMicros(gi) === null);
      if (missing.length) {
        toast.error(
          missing.length === op.adGroups.length
            ? "Enter a max CPC bid for each ad group — this campaign uses manual bidding."
            : `Enter a max CPC bid for ad group ${missing.map((n) => n + 1).join(", ")} — this campaign uses manual bidding.`,
        );
        return;
      }
    }
    setConfirmOp(op);
  };

  // Step 2 — user clicks Confirm in the gate: the ONLY place that writes to Google Ads.
  const doPublish = async () => {
    const op = confirmOp;
    if (!op || publishing || !ownClientId) return;
    setConfirmOp(null);
    setPublishing(true);

    // Preflight: confirm Google Ads is actually connected before pretending to publish.
    const { data: cRow } = await supabase
      .from("clients")
      .select("google_ads_connected_at")
      .eq("id", ownClientId)
      .maybeSingle();
    // Connected if THIS client holds tokens OR the shared operator connection resolved
    // (campaigns loaded ⇒ a connection is reachable — own or shared). The push path
    // does the authoritative resolution; this is just a friendly early check.
    const hasTokens = !!(cRow as any)?.google_ads_connected_at || accountCtx.campaigns.length > 0;
    if (!hasTokens) {
      setPublishing(false);
      toast.error("Google Ads is not connected.");
      setMessages((m) => [
        ...m,
        {
          id: crypto.randomUUID(),
          role: "assistant",
          content:
            "❌ **Google Ads is not connected.**\n\nNo OAuth tokens are stored for this account. Open **Dashboard → Integrations → Google Ads → Connect Google Ads Account**, complete Google's consent screen, then try Approve again.",
        },
      ]);
      return;
    }

    setMessages((m) => [
      ...m,
      { id: crypto.randomUUID(), role: "assistant", content: op.isAdd ? `⏳ Adding to **${op.targetCampaignName}**…` : "⏳ Creating a new campaign…" },
    ]);

    try {
      const { data, error } = await supabase.functions.invoke("push-google-ads-campaign", {
        body: {
          clientId: ownClientId,
          workspace,
          action: { op: op.op, targetCampaignId: op.targetCampaignId, targetCampaignName: op.targetCampaignName },
          campaignName: workspace.title || undefined,
          dailyBudgetUSD: 10,
          // Exact per-ad-group max-CPC bids in micros — ONLY for manual campaigns; the
          // server re-checks the strategy and ignores these unless the campaign is manual.
          // Never a default: null where the user set nothing (server refuses manual+null).
          adGroupBids: op.adGroups.map((_, gi) => (isManualBidding ? bidMicros(gi) : null)),
        },
      });

      // Try to recover body even if invoke flagged a transport error
      let payload: any = data;
      if (!payload && error) {
        try {
          const ctx: any = (error as any).context;
          if (ctx && typeof ctx.json === "function") payload = await ctx.json();
          else if (ctx && typeof ctx.text === "function") {
            const t = await ctx.text();
            try { payload = JSON.parse(t); } catch { payload = { ok: false, error: t }; }
          }
        } catch {}
      }

      if (!payload?.ok) {
        const step = payload?.step ? `\n• **Failing step:** \`${payload.step}\`` : "";
        const errMsg = payload?.error || error?.message || "Unknown error";
        const trace = Array.isArray(payload?.trace) ? payload.trace : [];
        const traceBlock = trace.length
          ? "\n\n**Trace:**\n" + trace.map((t: any) =>
              `${t.ok ? "✅" : "❌"} \`${t.step}\`${t.detail ? `\n\`\`\`json\n${JSON.stringify(t.detail, null, 2).slice(0, 1200)}\n\`\`\`` : ""}`
            ).join("\n")
          : "";
        const rawBlock = payload?.details
          ? `\n\n**Raw Google response:**\n\`\`\`json\n${JSON.stringify(payload.details, null, 2).slice(0, 2000)}\n\`\`\``
          : "";
        toast.error(`Google Ads push failed: ${errMsg}`);
        console.error("[Aria push] full payload:", payload);
        setMessages((m) => [
          ...m,
          {
            id: crypto.randomUUID(),
            role: "assistant",
            content: `❌ Google Ads rejected the push.\n\n**Error:** ${errMsg}${step}${rawBlock}${traceBlock}`,
          },
        ]);
        return;
      }

      const cid = payload.campaignId;
      // Full per-ad-group report — every group that went live and every one that didn't.
      const results: any[] = Array.isArray(payload.results) ? payload.results : [];
      const groupLines = results.map((r: any, i: number) => {
        if (!r.ok) return `❌ **Ad group ${i + 1} — ${r.adGroup}**: ${r.error || "failed"}`;
        const parts = [`${r.adsCreated} ad${r.adsCreated === 1 ? "" : "s"}`, `${r.keywordsCreated} keyword${r.keywordsCreated === 1 ? "" : "s"}`];
        const adFails = (r.ads || []).filter((a: any) => !a.ok);
        const skips = adFails.length
          ? "\n   " + adFails.map((a: any) => `⚠ Ad ${a.ad} ${a.skipped ? "skipped" : "rejected"}: ${a.reason}`).join("\n   ")
          : "";
        return `✅ **Ad group ${i + 1} — ${r.adGroup}** (${parts.join(", ")})${skips}`;
      }).join("\n");
      const slResults: any[] = Array.isArray(payload.sitelinkResults) ? payload.sitelinkResults : [];
      const slLines = slResults.length
        ? "\n\n**Sitelinks** (per ad group)\n" + slResults.map((s: any) =>
            `${s.ok ? "✅" : "❌"} ${s.adGroup ? `[${s.adGroup}] ` : ""}${s.text} → \`${s.url || "—"}\`${s.ok ? "" : ` — ${s.skipped ? "skipped" : "failed"}: ${s.reason}`}`
          ).join("\n")
        : "";
      const slCount = payload.sitelinksTotal ? ` · Sitelinks: **${payload.sitelinksCreated}/${payload.sitelinksTotal}**` : "";
      const header = payload.partial ? "⚠️ Published with some issues" : "✅ Published";
      const summary = `${header}\n\n• Campaign: \`${cid}\`${payload.mode === "add_to_existing" ? ` (added to **${op.targetCampaignName}**)` : " (**PAUSED** — enable it in Google Ads to start serving)"}\n• Ad groups live: **${payload.adGroupsCreated}/${payload.adGroupsTotal}** · Ads: **${payload.adsCreated}** · Keywords: **${payload.keywordsCreated}**${slCount}\n\n${groupLines}${slLines}${payload.partial ? "\n\nFix the flagged items in the preview and re-push just those — the live ones stay." : ""}`;
      toast[payload.partial ? "warning" : "success"](payload.partial ? `Published ${payload.adGroupsCreated}/${payload.adGroupsTotal} ad groups` : `Published ${payload.adGroupsCreated} ad group(s) to ${cid}`);
      setMessages((m) => [...m, { id: crypto.randomUUID(), role: "assistant", content: summary }]);
      // Keep the workspace open on partial so the user can fix + re-push; clear on full success.
      if (!payload.partial) setWorkspace(null);
    } catch (err: any) {
      console.error("Aria approve push error:", err);
      const msg = err?.message || "Unknown error";
      toast.error(`Google Ads push failed: ${msg}`);
      setMessages((m) => [
        ...m,
        {
          id: crypto.randomUUID(),
          role: "assistant",
          content: `❌ Unexpected error while pushing to Google Ads.\n\n**Error:** ${msg}`,
        },
      ]);
    } finally {
      setPublishing(false);
    }
  };


  // Unlimited regeneration — regenerate the whole build (fresh everything).
  const regenerateAd = () => {
    if (thinking) return;
    const base = lastAdPromptRef.current;
    if (!base) return;
    const prompt = `Regenerate the whole build. Same brief as before ("${base}"), same ad groups, but produce completely different headlines, descriptions, sitelinks and a fresh creative angle. Do not repeat anything from before.`;
    handleSend(prompt, undefined, "advanced", true);
  };

  // PER-SITELINK regeneration — swap ONE sitelink IN PLACE for a different valid one the
  // engine picks from the site's real pages. No AI round-trip, so nothing flips: the ad
  // group, keywords, ads and the other three sitelinks are untouched. Publish stays healthy.
  const regenerateSitelinkAt = (gi: number, targetSitelink: any) => {
    if (!workspace || workspace.type !== "ad") return;
    const adGroups = workspace.payload.adGroups || [];
    const g = adGroups[gi];
    if (!g) return;
    const replacement = pickReplacementSitelink(g.adGroupName, siteInventory, g.sitelinks);
    if (!replacement) { toast.error("No other sitelink available for this ad group — every real page is already used."); return; }
    setWorkspace((prev: any) => {
      if (!prev || prev.type !== "ad") return prev;
      const nextGroups = (prev.payload.adGroups || []).map((grp: any, i: number) =>
        i === gi ? { ...grp, sitelinks: grp.sitelinks.map((s: any) => (s.text === targetSitelink.text && s.url === targetSitelink.url ? replacement : s)) } : grp);
      return { ...prev, payload: { ...prev.payload, adGroups: nextGroups } };
    });
  };

  // ── REMOVE (delete only — no ban) any item from the current build ──
  const deleteAdGroup = (gi: number) => {
    setWorkspace((prev: any) => {
      if (!prev || prev.type !== "ad") return prev;
      const adGroups = normalizeAdGroups(prev.payload, siteInventory).filter((_, i) => i !== gi);
      return { ...prev, payload: { adGroups } };
    });
  };
  const deleteAd = (gi: number, ai: number) => {
    setWorkspace((prev: any) => {
      if (!prev || prev.type !== "ad") return prev;
      const adGroups = normalizeAdGroups(prev.payload, siteInventory).map((g, i) =>
        i === gi ? { ...g, ads: g.ads.filter((_, j) => j !== ai) } : g);
      return { ...prev, payload: { adGroups } };
    });
  };
  const deleteSitelink = (gi: number, si: number) => {
    setWorkspace((prev: any) => {
      if (!prev || prev.type !== "ad") return prev;
      const adGroups = normalizeAdGroups(prev.payload, siteInventory).map((g, i) =>
        i === gi ? { ...g, sitelinks: g.sitelinks.filter((_, j) => j !== si) } : g);
      return { ...prev, payload: { adGroups } };
    });
  };

  return (
    <div
      className={`grid w-full min-w-0 gap-4 ${mode === "advanced" ? "grid-cols-1 lg:grid-cols-2" : "grid-cols-1"}`}
    >
      {/* ── CONFIRMATION GATE — the ONLY thing that can trigger a Google Ads write ── */}
      {confirmOp && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 p-4"
          onClick={() => setConfirmOp(null)}
        >
          <div
            className="flex max-h-[88vh] w-full max-w-lg flex-col rounded-2xl border p-5 text-white"
            style={{ background: "#14110e", borderColor: `${BURNT}55`, boxShadow: "0 20px 60px rgba(0,0,0,0.6)" }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Operation TYPE — catch a create-you-didn't-want by eye */}
            {confirmOp.isAdd ? (
              <div className="mb-3 flex items-center gap-2 rounded-lg px-3 py-2" style={{ background: `${GREEN}1f`, border: `1px solid ${GREEN}66` }}>
                <CheckCircle2 size={16} style={{ color: GREEN }} />
                <div className="text-[13px] font-extrabold" style={{ color: GREEN }}>Add to “{confirmOp.targetCampaignName}”</div>
              </div>
            ) : (
              <div className="mb-3 flex items-center gap-2 rounded-lg px-3 py-2" style={{ background: `${ORANGE}1f`, border: `1px solid ${ORANGE}66` }}>
                <AlertTriangle size={16} style={{ color: ORANGE }} />
                <div className="text-[13px] font-extrabold" style={{ color: ORANGE }}>Create a new campaign</div>
              </div>
            )}

            <div className="mb-2 text-[12px] text-white/60">
              {confirmOp.isAdd
                ? `Adds ${confirmOp.totals.adGroups} ad group${confirmOp.totals.adGroups === 1 ? "" : "s"} to an existing campaign. No new campaign, no budget change.`
                : `Creates a brand-new PAUSED campaign (budget + campaign) with ${confirmOp.totals.adGroups} ad group${confirmOp.totals.adGroups === 1 ? "" : "s"}. Only confirm if you meant to create one.`}
            </div>

            {/* Full scope summary */}
            <div className="mb-3 flex flex-wrap gap-1.5 text-[11px]">
              <span className="rounded-md bg-white/[0.06] px-2 py-1 font-semibold">{confirmOp.totals.adGroups} ad group{confirmOp.totals.adGroups === 1 ? "" : "s"}</span>
              <span className="rounded-md bg-white/[0.06] px-2 py-1 font-semibold">{confirmOp.totals.ads} ad{confirmOp.totals.ads === 1 ? "" : "s"}</span>
              <span className="rounded-md bg-white/[0.06] px-2 py-1 font-semibold">{confirmOp.totals.keywords} keyword{confirmOp.totals.keywords === 1 ? "" : "s"}</span>
              {confirmOp.totals.sitelinks > 0 && (
                <span className="rounded-md bg-white/[0.06] px-2 py-1 font-semibold">{confirmOp.totals.sitelinks} sitelink{confirmOp.totals.sitelinks === 1 ? "" : "s"}</span>
              )}
            </div>

            {confirmOp.totals.issues > 0 && (
              <div className="mb-3 flex items-start gap-2 rounded-lg px-3 py-2 text-[11.5px]" style={{ background: `${ORANGE}1f`, border: `1px solid ${ORANGE}66`, color: ORANGE }}>
                <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                <span>{confirmOp.totals.issues} item{confirmOp.totals.issues === 1 ? "" : "s"} exceed Google's limits (flagged below). They'll be skipped and reported — everything valid still publishes.</span>
              </div>
            )}

            {confirmOp.isAdd && (
              <div className="mb-3 flex justify-between gap-3 text-[12.5px]">
                <span className="text-white/50">Target campaign</span>
                <span className="text-right font-semibold">
                  {confirmOp.targetCampaignName} <span className="font-mono text-white/50">({confirmOp.targetCampaignId})</span>
                </span>
              </div>
            )}

            {/* Every ad group → its ads → its keywords. Scrolls; nothing truncated. */}
            <div className="min-h-0 flex-1 space-y-3 overflow-y-auto pr-1">
              {confirmOp.adGroups.map((g, gi) => {
                const gIssues = validateAdGroup(g);
                return (
                  <div key={gi} className="rounded-lg border border-white/10 bg-white/[0.03] p-3">
                    <div className="mb-1.5 flex items-center gap-2">
                      <span className="rounded bg-white/[0.08] px-1.5 py-0.5 text-[10px] font-bold text-white/60">AD GROUP {gi + 1}</span>
                      <span className="text-[12.5px] font-bold">{g.adGroupName}</span>
                      {gIssues.length > 0 && <AlertTriangle size={13} style={{ color: ORANGE }} />}
                    </div>
                    {/* Bid: exactly what will be sent — the user's value (manual) or none. */}
                    <div className="mb-1.5 text-[10.5px] text-white/50">
                      {isManualBidding
                        ? <>Max CPC bid: <span className="font-mono text-white/80">€{bids[gi] ?? "—"}</span> <span className="text-white/40">(sent as entered)</span></>
                        : isImpressionShareBidding
                          ? "No ad-group bid — Target Impression Share is managed at campaign level."
                          : "No ad-group bid — managed by the campaign's bidding strategy."}
                    </div>
                    {g.keywords.length > 0 && (
                      <div className="mb-2 flex flex-wrap gap-1">
                        {g.keywords.map((k, ki) => (
                          <span key={ki} className="flex items-center gap-1 rounded bg-white/[0.05] px-1.5 py-0.5 text-[10.5px]">
                            <span className="font-mono">{k.text}</span>
                            <span className="rounded border border-white/15 px-1 text-[9px] uppercase text-white/55">{k.matchType}</span>
                          </span>
                        ))}
                      </div>
                    )}
                    <div className="space-y-1.5">
                      {g.ads.map((ad, ai) => {
                        const issues = validateAd(ad);
                        return (
                          <div key={ai} className="rounded-md bg-white/[0.03] px-2 py-1.5 text-[11px]">
                            <div className="flex items-center justify-between">
                              <span className="font-semibold text-white/80">Ad {ai + 1} — RSA</span>
                              <span className="text-white/45">{ad.headlines.length} headlines · {ad.descriptions.length} descriptions</span>
                            </div>
                            {issues.length > 0 && (
                              <ul className="mt-1 space-y-0.5" style={{ color: ORANGE }}>
                                {issues.map((iss, ii) => <li key={ii}>⚠ {iss}</li>)}
                              </ul>
                            )}
                          </div>
                        );
                      })}
                      {g.ads.length === 0 && <div className="text-[11px] text-white/40">No ads (keywords only).</div>}
                    </div>
                    {g.sitelinks.length > 0 && (
                      <div className="mt-2">
                        <div className="mb-1 text-[10px] font-bold uppercase tracking-wider text-white/45">Sitelinks</div>
                        <div className="space-y-1">
                          {g.sitelinks.map((s, si) => {
                            const issues = validateSitelink(s);
                            return (
                              <div key={si} className="rounded-md bg-white/[0.03] px-2 py-1.5 text-[11px]">
                                <div className="flex items-center justify-between gap-3">
                                  <span className="flex items-center gap-1.5">
                                    <span className="rounded px-1 text-[8px] font-bold uppercase" style={s.kind === "main" ? { background: `${BLUE}22`, color: BLUE } : { background: `${BURNT}22`, color: BURNT_GLOW }}>{s.kind === "main" ? "Main" : "Specific"}</span>
                                    <span className="font-semibold" style={sitelinkTextTooLong(s.text) ? { color: ORANGE } : undefined}>{s.text}{sitelinkTextTooLong(s.text) && <span className="ml-1 font-bold">{s.text.length}/{SITELINK_TEXT_MAX}</span>}</span>
                                  </span>
                                  <span className="break-all text-right font-mono text-[10px] text-white/55">{s.url || "—"}</span>
                                </div>
                                {issues.length > 0 && (
                                  <ul className="mt-1 space-y-0.5" style={{ color: ORANGE }}>
                                    {issues.map((iss, ii) => <li key={ii}>⚠ {iss}</li>)}
                                  </ul>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="mt-4 flex shrink-0 justify-end gap-2 border-t border-white/10 pt-4">
              <button
                onClick={() => setConfirmOp(null)}
                className="rounded-lg border border-white/12 bg-white/[0.04] px-3 py-2 text-[12px] font-semibold text-white/80 hover:bg-white/[0.08]"
              >
                Cancel
              </button>
              <button
                onClick={doPublish}
                className="flex items-center gap-1.5 rounded-lg px-3.5 py-2 text-[12px] font-extrabold text-white"
                style={{ background: confirmOp.isAdd ? GREEN : ORANGE, boxShadow: `0 6px 18px ${(confirmOp.isAdd ? GREEN : ORANGE)}55` }}
              >
                <CheckCircle2 size={13} />
                {confirmOp.isAdd ? `Confirm — add to ${confirmOp.targetCampaignName}` : "Confirm — create new campaign"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* LEFT: chat */}
      <div
        className="flex min-w-0 flex-col rounded-2xl h-[70dvh] min-h-[480px] lg:h-[calc(100vh-260px)] lg:min-h-[560px]"
        style={glass}
      >
        {/* Header */}
        <div data-tour-id="aria-title" className="flex items-center justify-between gap-2 border-b border-white/5 px-3 py-3 sm:px-5 sm:py-4">
          <div className="flex items-center gap-3">
            <div
              className="relative flex h-10 w-10 items-center justify-center rounded-xl"
              style={{
                background: `${BURNT}1f`,
                border: `1px solid ${BURNT}55`,
                boxShadow: `0 0 24px ${BURNT}66`,
              }}
            >
              <Sparkles size={16} style={{ color: BURNT_GLOW }} />
              <span
                className="absolute inset-0 rounded-xl"
                style={{ boxShadow: `inset 0 0 18px ${BURNT}44`, animation: "pulse 2.4s ease-in-out infinite" }}
              />
            </div>
            <div>
              <h2 className="text-[15px] font-bold tracking-tight text-white">ARIA</h2>
              <p className="text-[11px] text-white/50">Your AI Google Ads expert · Online</p>
            </div>
          </div>

          {/* ACTIVE CAMPAIGN selector — center of the top bar. Drives what Aria works on. */}
          <div className="relative shrink-0">
            <button
              type="button"
              onClick={() => setSelectorOpen((o) => !o)}
              disabled={accountCtx.campaigns.length === 0}
              title={activeCampaign?.name || undefined}
              className="flex max-w-[150px] items-center gap-1.5 rounded-full border px-3 py-1.5 text-[12px] font-semibold transition-all disabled:opacity-50 sm:max-w-[220px]"
              style={{ borderColor: `${BURNT}55`, background: `${BURNT}14`, color: "#fff" }}
            >
              <Target size={12} style={{ color: BURNT_GLOW }} className="shrink-0" />
              <span className="truncate">{activeCampaign?.name || (accountCtx.campaigns.length ? "Select campaign" : "No campaigns")}</span>
              <ChevronDown size={12} className={`shrink-0 transition-transform ${selectorOpen ? "rotate-180" : ""}`} />
            </button>

            {selectorOpen && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setSelectorOpen(false)} />
                <div className="absolute left-1/2 top-full z-50 mt-2 w-72 max-w-[85vw] -translate-x-1/2 rounded-xl border border-white/10 p-2 shadow-2xl" style={{ background: "#14110e" }}>
                  <div className="relative mb-2">
                    <Search size={13} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-white/40" />
                    <input
                      value={campaignQuery}
                      onChange={(e) => setCampaignQuery(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Escape") setSelectorOpen(false); }}
                      autoFocus
                      placeholder="Search all campaigns…"
                      className="w-full rounded-lg border border-white/10 bg-white/[0.04] py-1.5 pl-8 pr-2 text-[12.5px] text-white placeholder:text-white/35 focus:border-white/20 focus:outline-none"
                    />
                  </div>
                  <div className="max-h-64 overflow-y-auto">
                    {(() => {
                      const q = campaignQuery.trim().toLowerCase();
                      const withId = accountCtx.campaigns.filter((c) => c.id);
                      let list: typeof withId;
                      if (q) {
                        list = withId
                          .slice()
                          .sort((a, b) => {
                            const aStarts = a.name.toLowerCase().startsWith(q);
                            const bStarts = b.name.toLowerCase().startsWith(q);
                            if (aStarts && !bStarts) return -1;
                            if (!aStarts && bStarts) return 1;
                            return a.name.localeCompare(b.name);
                          })
                          .filter((c) => c.name.toLowerCase().includes(q))
                          .slice(0, 40);
                      } else {
                        const recent = recentIds.map((id) => withId.find((c) => String(c.id) === id)).filter(Boolean) as typeof withId;
                        list = (recent.length ? recent : withId).slice(0, 5);
                      }
                      return (
                        <>
                          <div className="px-2 pb-1 pt-0.5 text-[10px] font-bold uppercase tracking-wider text-white/35">
                            {q ? `${list.length} match${list.length === 1 ? "" : "es"}` : "Recent"}
                          </div>
                          {list.length === 0 && (
                            <div className="px-2 py-3 text-[12px] text-white/45">No campaigns match “{campaignQuery}”.</div>
                          )}
                          {list.map((c) => (
                            <button
                              key={String(c.id)}
                              type="button"
                              onClick={() => pickCampaign(c)}
                              className="flex w-full items-center justify-between gap-2 rounded-lg px-2 py-1.5 text-left text-[12.5px] text-white/85 hover:bg-white/[0.06]"
                            >
                              <span className="truncate">{c.name}</span>
                              {activeCampaign?.id === String(c.id) && <Check size={13} className="shrink-0" style={{ color: GREEN }} />}
                            </button>
                          ))}
                        </>
                      );
                    })()}
                  </div>

                  {/* ② Per-campaign ad domain — resolution: override → from ads → account default */}
                  {activeCampaign && (
                    <div className="mt-2 border-t border-white/10 pt-2">
                      <div className="mb-1 flex items-center justify-between px-1">
                        <span className="text-[10px] font-bold uppercase tracking-wider text-white/35">Ad domain</span>
                        <span className="text-[10px] text-white/40">
                          {activeCampaignRow?.overrideDomain ? "override" : activeCampaignRow?.adDomain ? "from ads" : "account default"}
                        </span>
                      </div>
                      <div className="mb-1.5 px-1 text-[12px] font-semibold" style={{ color: BURNT_GLOW }}>{activeDomain || "— none —"}</div>
                      <div className="flex items-center gap-1.5">
                        <input
                          value={domainDraft}
                          onChange={(e) => setDomainDraft(e.target.value)}
                          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); saveDomainOverride(domainDraft); } }}
                          placeholder={activeCampaignRow?.adDomain ? `Override (ads: ${activeCampaignRow.adDomain})` : "Set this campaign's domain…"}
                          className="min-w-0 flex-1 rounded-lg border border-white/10 bg-white/[0.04] px-2.5 py-1.5 text-[12px] text-white placeholder:text-white/30 focus:border-white/25 focus:outline-none"
                        />
                        <button type="button" onClick={() => saveDomainOverride(domainDraft)} className="rounded-md px-2.5 py-1.5 text-[11px] font-bold text-white" style={{ background: BURNT }}>Save</button>
                        {activeCampaignRow?.overrideDomain && (
                          <button type="button" onClick={() => { setDomainDraft(""); saveDomainOverride(""); }} className="rounded-md border border-white/12 px-2 py-1.5 text-[11px] text-white/60 hover:bg-white/[0.06]" title="Clear override — use the campaign's own ad domain">Clear</button>
                        )}
                      </div>
                      <div className="mt-1 px-1 text-[10px] text-white/35">Ads &amp; sitelinks Aria builds for this campaign use this domain.</div>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>

          {/* Mode toggle */}
          <div className="flex items-center rounded-full border border-white/10 bg-white/[0.03] p-0.5">
            {(["standard", "advanced"] as Mode[]).map((m) => {
              const active = mode === m;
              const isAdv = m === "advanced";
              return (
                <button
                  key={m}
                  onClick={() => requestMode(m)}
                  className="flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11.5px] font-bold capitalize transition-all"
                  style={{
                    background: active ? (isAdv ? BURNT : "rgba(255,255,255,0.08)") : "transparent",
                    color: active ? (isAdv ? "#fff" : "#fff") : "rgba(255,255,255,0.55)",
                    boxShadow: active && isAdv ? `0 6px 20px ${BURNT}77` : "none",
                  }}
                >
                  {isAdv && <AlertTriangle size={11} />}
                  {m}
                </button>
              );
            })}
          </div>
        </div>

        {/* Messages */}
        <div ref={scrollRef} className="flex-1 space-y-4 overflow-y-auto px-3 py-4 sm:px-5 sm:py-5">
          {messages.map((m) => (
            <div key={m.id} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"} animate-fade-in`}>
              <div
                className="max-w-[85%] rounded-2xl px-4 py-2.5 text-[13.5px] leading-relaxed"
                style={
                  m.role === "user"
                    ? { background: BURNT, color: "#fff", boxShadow: `0 8px 24px ${BURNT}55` }
                    : {
                        background: "rgba(255,255,255,0.04)",
                        color: "rgba(255,255,255,0.88)",
                        border: "1px solid rgba(255,255,255,0.06)",
                      }
                }
              >
                {m.content.split(/(\*\*[^*]+\*\*|\*[^*]+\*)/g).map((c, i) => {
                  if (c.startsWith("**") && c.endsWith("**"))
                    return <strong key={i} className="font-bold text-white">{c.slice(2, -2)}</strong>;
                  if (c.startsWith("*") && c.endsWith("*"))
                    return <em key={i} className="italic text-white/90">{c.slice(1, -1)}</em>;
                  return <span key={i}>{c}</span>;
                })}
              </div>
            </div>
          ))}
          {thinking && (
            <div className="flex justify-start">
              <div
                className="flex items-center gap-2 rounded-2xl px-4 py-2.5 text-[13px] text-white/60"
                style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.06)" }}
              >
                <span className="flex gap-1">
                  <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-white/70" style={{ animationDelay: "0ms" }} />
                  <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-white/70" style={{ animationDelay: "150ms" }} />
                  <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-white/70" style={{ animationDelay: "300ms" }} />
                </span>
                ARIA is thinking…
              </div>
            </div>
          )}

          {/* Suggested — inline so it flows with the intro instead of overlapping it */}
          {messages.length <= 1 && (
            <div data-tour-id="aria-suggested" className="pt-3 sm:pt-5">
              <div className="mb-2.5 text-[10.5px] font-bold uppercase tracking-[0.14em] text-white/40">Suggested</div>
              <div className="flex flex-wrap gap-2">
                {SUGGESTED.map((s) => {
                  const Icon = s.icon;
                  return (
                    <button
                      key={s.label}
                      onClick={() => handleSend(s.label)}
                      className="group flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-3 py-1.5 text-[12px] font-semibold text-white/75 transition-all hover:-translate-y-0.5 hover:border-white/25 hover:bg-white/[0.06] hover:text-white"
                      style={{ boxShadow: `inset 0 0 0 0 ${BURNT}` }}
                    >
                      <Icon size={12} style={{ color: BURNT_GLOW }} />
                      {s.label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* ── BUILD ASSEMBLER — structured build actions (Advanced mode + active campaign).
             Assemble ad groups/keywords/ads/sitelinks, then Generate reuses handleSend so
             the result flows into the EXISTING Live Workspace preview + confirm gate. ── */}
        {mode === "advanced" && activeCampaign && (
          <div className="border-t border-white/5 px-3 pt-2.5 sm:px-5">
            <button
              type="button"
              onClick={() => setAssemblerOpen((o) => !o)}
              className="mb-1.5 flex w-full items-center justify-between text-left"
            >
              <span className="flex items-center gap-2">
                <Zap size={13} style={{ color: BURNT_GLOW }} />
                <span className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-white/50">Build</span>
                {plan.groups.length > 0 && (
                  <span className="rounded-full bg-white/[0.06] px-1.5 py-0.5 text-[10px] font-semibold text-white/60">
                    {plan.groups.length} group{plan.groups.length === 1 ? "" : "s"} · {plan.groups.reduce((n, g) => n + g.ads, 0)} ad{plan.groups.reduce((n, g) => n + g.ads, 0) === 1 ? "" : "s"}{plan.sitelinks ? " · sitelinks" : ""}
                  </span>
                )}
              </span>
              <ChevronDown size={14} className={`text-white/40 transition-transform ${assemblerOpen ? "" : "-rotate-90"}`} />
            </button>

            {assemblerOpen && (
              <div className="animate-fade-in space-y-2.5 pb-2.5">
                {/* Action palette */}
                <div className="flex flex-wrap gap-1.5">
                  {[
                    { key: "group", label: "Ad Group", icon: Layers, onClick: startAddGroup },
                    { key: "kw", label: "Keywords", icon: KeyRound, onClick: startAddKeywords },
                    { key: "ads", label: "Ads", icon: Megaphone, onClick: bumpActiveAds },
                  ].map((a) => (
                    <button
                      key={a.key}
                      type="button"
                      onClick={a.onClick}
                      className="group flex items-center gap-1 rounded-full border border-white/10 bg-white/[0.03] px-2.5 py-1.5 text-[12px] font-semibold text-white/80 transition-all hover:-translate-y-0.5 hover:border-white/25 hover:bg-white/[0.06] hover:text-white"
                    >
                      <a.icon size={12} style={{ color: BURNT_GLOW }} /><Plus size={9} className="opacity-50" />{a.label}
                    </button>
                  ))}
                  <button
                    type="button"
                    onClick={() => setPlan((p) => ({ ...p, sitelinks: !p.sitelinks }))}
                    title="Sitelinks per ad group — 2 specific + 2 main"
                    className="flex items-center gap-1 rounded-full border px-2.5 py-1.5 text-[12px] font-semibold transition-all hover:-translate-y-0.5"
                    style={plan.sitelinks
                      ? { borderColor: `${GREEN}66`, background: `${GREEN}1a`, color: GREEN }
                      : { borderColor: "rgba(255,255,255,0.1)", background: "rgba(255,255,255,0.03)", color: "rgba(255,255,255,0.8)" }}
                  >
                    <Link2 size={12} />Sitelinks{plan.sitelinks ? <Check size={11} /> : null}
                  </button>
                </div>

                {/* Inline draft input — group name or keyword */}
                {draftMode && (
                  <div className="animate-fade-in flex items-center gap-1.5 rounded-lg border border-white/15 bg-white/[0.04] px-2.5 py-1.5">
                    {draftMode === "keyword" && (
                      <button
                        type="button"
                        onClick={() => setDraftMatch((m) => (m === "Exact" ? "Phrase" : m === "Phrase" ? "Broad" : "Exact"))}
                        className="rounded border border-white/15 px-1.5 py-0.5 text-[10px] font-bold uppercase text-white/70 hover:bg-white/[0.08]"
                        title="Click to cycle match type"
                      >
                        {draftMatch}
                      </button>
                    )}
                    <input
                      autoFocus
                      value={draftText}
                      onChange={(e) => setDraftText(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") { e.preventDefault(); draftMode === "group" ? commitGroup() : commitKeywords(); }
                        if (e.key === "Escape") { setDraftMode(null); setDraftText(""); }
                      }}
                      placeholder={draftMode === "group" ? "Ad group name (e.g. Lock Rekey)…" : "Keyword — comma-separate for several…"}
                      className="flex-1 bg-transparent text-[12.5px] text-white placeholder:text-white/35 focus:outline-none"
                    />
                    <button type="button" onClick={() => (draftMode === "group" ? commitGroup() : commitKeywords())} className="rounded-md px-2 py-1 text-[11px] font-bold text-white" style={{ background: BURNT }}>Add</button>
                    <button type="button" onClick={() => { setDraftMode(null); setDraftText(""); }} className="text-white/40 hover:text-white/80"><X size={13} /></button>
                  </div>
                )}

                {/* Plan — queued ad groups */}
                {plan.groups.length === 0 ? (
                  <div className="rounded-lg border border-dashed border-white/10 px-3 py-3 text-center text-[11.5px] text-white/40">
                    Assemble your build — add an ad group, its keywords and ads, then Generate.
                  </div>
                ) : (
                  <div className="max-h-[30vh] space-y-1.5 overflow-y-auto pr-1">
                    {plan.groups.map((g, gi) => {
                      const active = g.id === activeGroupId;
                      return (
                        <div
                          key={g.id}
                          onClick={() => setActiveGroupId(g.id)}
                          className="animate-scale-in cursor-pointer rounded-lg border p-2.5 transition-colors"
                          style={active ? { borderColor: `${BURNT}88`, background: `${BURNT}12` } : { borderColor: "rgba(255,255,255,0.1)", background: "rgba(255,255,255,0.02)" }}
                        >
                          <div className="flex items-center justify-between gap-2">
                            <span className="flex min-w-0 items-center gap-2">
                              <span className="rounded bg-white/[0.08] px-1.5 py-0.5 text-[9px] font-bold text-white/55">{gi + 1}</span>
                              <span className="truncate text-[12.5px] font-bold text-white/90">{g.name}</span>
                            </span>
                            <span className="flex shrink-0 items-center gap-1.5">
                              <button type="button" onClick={(e) => { e.stopPropagation(); cycleAds(g.id); }} className="rounded border border-white/15 px-1.5 py-0.5 text-[10px] font-semibold text-white/75 hover:bg-white/[0.08]" title="Ads in this group — click to cycle 1–3">
                                {g.ads} ad{g.ads === 1 ? "" : "s"}
                              </button>
                              <button type="button" onClick={(e) => { e.stopPropagation(); removeGroup(g.id); }} className="text-white/35 hover:text-red-400"><Trash2 size={12} /></button>
                            </span>
                          </div>
                          {g.keywords.length > 0 && (
                            <div className="mt-1.5 flex flex-wrap gap-1">
                              {g.keywords.map((k, ki) => (
                                <span key={ki} className="flex items-center gap-1 rounded bg-white/[0.05] px-1.5 py-0.5 text-[10px] text-white/80">
                                  <span className="font-mono">{k.text}</span>
                                  <span className="rounded border border-white/15 px-1 text-[8.5px] uppercase text-white/50">{k.match}</span>
                                  <button type="button" onClick={(e) => { e.stopPropagation(); removeKeyword(g.id, ki); }} className="text-white/30 hover:text-red-400"><X size={9} /></button>
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Generate + Clear · Create New Campaign (separate, placeholder) */}
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={generateFromPlan}
                    disabled={thinking || plan.groups.length === 0}
                    className="flex items-center gap-1.5 rounded-lg px-3.5 py-2 text-[12.5px] font-extrabold text-white transition-all disabled:opacity-40"
                    style={{ background: BURNT, boxShadow: plan.groups.length ? `0 6px 18px ${BURNT}66` : "none" }}
                  >
                    <Zap size={13} />Generate
                  </button>
                  {plan.groups.length > 0 && (
                    <button type="button" onClick={clearPlan} className="rounded-lg border border-white/12 bg-white/[0.03] px-3 py-2 text-[12px] font-semibold text-white/70 hover:bg-white/[0.07]">Clear</button>
                  )}
                  <div className="flex-1" />
                  <button
                    type="button"
                    onClick={() => toast("Create Campaign is coming next — strategy, budget, language & networks.")}
                    className="flex items-center gap-1.5 rounded-lg border border-dashed px-3 py-2 text-[12px] font-semibold transition-colors hover:bg-white/[0.03]"
                    style={{ borderColor: `${BLUE}55`, color: BLUE }}
                  >
                    <Plus size={12} />Create New Campaign
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Input */}
        <div data-tour-id="aria-input" className="border-t border-white/5 px-3 py-3 sm:px-5 sm:py-4">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleSend(input);
            }}
            className="flex items-center gap-2 rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2 transition-colors focus-within:border-white/30"
          >
            <button type="button" className="text-white/40 hover:text-white/80" aria-label="Attach">
              <Paperclip size={15} />
            </button>
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={mode === "advanced" ? "Tell ARIA what to build…" : "Ask ARIA anything about your account…"}
              className="flex-1 bg-transparent text-[13.5px] text-white placeholder:text-white/35 focus:outline-none"
            />
            <button
              type="button"
              onClick={toggleRecording}
              disabled={transcribing}
              className={`relative flex h-7 w-7 items-center justify-center rounded-md transition-colors disabled:opacity-50 ${
                isRecording ? "text-red-500" : "text-white/40 hover:text-white/80"
              }`}
              aria-label={isRecording ? "Stop recording" : "Start voice input"}
            >
              {transcribing ? (
                <Loader2 size={15} className="animate-spin" />
              ) : (
                <>
                  <Mic size={15} className="relative" />
                  {isRecording && (
                    <span className="absolute -top-0.5 -right-0.5 flex h-2 w-2">
                      <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-500 opacity-75" />
                      <span className="relative inline-flex h-2 w-2 rounded-full bg-red-500" />
                    </span>
                  )}
                </>
              )}
            </button>
            <button
              type="submit"
              disabled={!input.trim()}
              className="flex h-8 w-8 items-center justify-center rounded-lg text-white transition-all disabled:opacity-30"
              style={{
                background: BURNT,
                boxShadow: input.trim() ? `0 6px 18px ${BURNT}77` : "none",
              }}
            >
              <Send size={13} />
            </button>
          </form>
        </div>
      </div>

      {/* RIGHT: workspace */}
      {mode === "advanced" && (
        <div
          data-tour-id="aria-workspace"
          className="flex min-w-0 flex-col rounded-2xl h-[70dvh] min-h-[480px] lg:h-[calc(100vh-260px)] lg:min-h-[560px]"
          style={glass}
        >
          <div className="flex flex-wrap items-start justify-between gap-2 border-b border-white/5 px-3 py-3 sm:px-5 sm:py-4">
            <div className="min-w-0 flex-1">
              <div className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-white/45">Live workspace</div>
              <div className="truncate text-[14px] font-bold text-white">{workspace?.title || "Awaiting input"}</div>
            </div>
            {workspace && (
              <div className="flex flex-wrap justify-end gap-1.5">
                <button
                  onClick={() => setWorkspace(null)}
                  className="flex items-center gap-1 rounded-lg border border-white/10 bg-white/[0.03] px-2.5 py-1.5 text-[11.5px] font-semibold text-white/75 hover:bg-white/[0.06]"
                >
                  <RefreshCw size={11} /> Reset
                </button>
                {workspace.type === "ad" && lastAdPromptRef.current && (
                  <>
                    <button
                      onClick={regenerateAd}
                      disabled={thinking}
                      className="flex items-center gap-1 rounded-lg border border-white/10 bg-white/[0.03] px-2.5 py-1.5 text-[11.5px] font-semibold text-white/75 transition-colors hover:bg-white/[0.06] disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      <Sparkles size={11} />
                      <span className="hidden sm:inline">Regenerate build</span>
                      <span className="sm:hidden">Regen</span>
                    </button>
                  </>
                )}
                <button
                  onClick={requestPublish}
                  disabled={publishing}
                  className="flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-[11.5px] font-bold text-white disabled:cursor-not-allowed disabled:opacity-60"
                  style={{ background: BURNT, boxShadow: `0 6px 18px ${BURNT}66` }}
                >
                  {publishing ? <Loader2 size={11} className="animate-spin" /> : <CheckCircle2 size={11} />}
                  {publishing ? "Publishing…" : "Review & publish"}
                </button>
              </div>
            )}
          </div>
          <div className="flex-1 overflow-y-auto p-3 sm:p-5">
            {!workspace ? (
              <div className="flex h-full flex-col items-center justify-center text-center text-white/45">
                <div
                  className="mb-3 flex h-14 w-14 items-center justify-center rounded-2xl"
                  style={{ background: `${BURNT}1a`, border: `1px solid ${BURNT}44` }}
                >
                  <Sparkles size={20} style={{ color: BURNT_GLOW }} />
                </div>
                <p className="text-[13px] text-white/70">Workspace will populate as you chat.</p>
                <p className="mt-1 text-[11.5px]">Ask ARIA to build an ad, suggest keywords, or reallocate budget.</p>
              </div>
            ) : workspace.type === "ad" ? (
              <div className="animate-scale-in space-y-4">
                {/* MULTI AD GROUP — every ad group, every RSA, fully expanded. Scrolls; nothing hidden. */}
                {normalizeAdGroups(workspace.payload, siteInventory).map((g: AdGroupSpec, gi: number) => {
                  const gIssues = validateAdGroup(g);
                  return (
                    <div key={gi} className="space-y-2.5 rounded-xl border border-white/10 bg-white/[0.02] p-3">
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="rounded bg-white/[0.08] px-1.5 py-0.5 text-[9.5px] font-bold tracking-wide text-white/55">
                            AD GROUP {gi + 1}
                          </span>
                          <span className="text-[13px] font-bold text-white/90">{g.adGroupName}</span>
                          {gIssues.length > 0 && (
                            <span className="flex items-center gap-1 text-[10px] font-semibold" style={{ color: ORANGE }}>
                              <AlertTriangle size={11} /> {gIssues.length} issue{gIssues.length === 1 ? "" : "s"}
                            </span>
                          )}
                        </div>
                        <button type="button" onClick={() => deleteAdGroup(gi)} title="Remove this ad group" className="shrink-0 text-white/30 hover:text-red-400"><Trash2 size={13} /></button>
                      </div>

                      {/* BID — driven by the active campaign's REAL strategy. Manual → you
                          enter the exact max-CPC bid (never invented). Impression-share /
                          automated → no ad-group bid is set (campaign governs it). */}
                      {isManualBidding ? (
                        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-white/10 bg-white/[0.02] px-2.5 py-2">
                          <span className="text-[10px] font-bold uppercase tracking-wider text-white/45">Max CPC bid</span>
                          <div className="flex items-center gap-1 rounded-md border border-white/15 bg-black/20 px-2 py-1">
                            <span className="text-[12px] text-white/50">€</span>
                            <input
                              type="number" min="0" step="0.01" inputMode="decimal"
                              value={bids[gi] ?? ""}
                              onChange={(e) => setBids((b) => ({ ...b, [gi]: e.target.value }))}
                              placeholder="0.00"
                              className="w-20 bg-transparent text-[13px] text-white outline-none placeholder:text-white/30"
                            />
                          </div>
                          <span className="text-[10px] text-white/40">manual bidding — this exact value is sent{bidMicros(gi) === null ? " · required" : ""}</span>
                          {bidMicros(gi) === null && <AlertTriangle size={11} style={{ color: ORANGE }} />}
                        </div>
                      ) : activeCampaignRow ? (
                        <div className="rounded-lg border border-white/10 bg-white/[0.02] px-2.5 py-1.5 text-[10.5px] text-white/45">
                          {isImpressionShareBidding
                            ? "Target Impression Share — bid is managed at the campaign level. No ad-group or keyword bid will be set."
                            : `${activeCampaignRow.strat || "Automated"} bidding — bids are managed by the campaign strategy. No ad-group bid will be set.`}
                        </div>
                      ) : null}

                      {g.keywords.length > 0 && (
                        <div className="flex flex-wrap gap-1">
                          {g.keywords.map((k, ki) => (
                            <span key={ki} className="flex items-center gap-1 rounded bg-white/[0.05] px-1.5 py-0.5 text-[10px] text-white/80">
                              <span className="font-mono">{k.text}</span>
                              <span className="rounded border border-white/15 px-1 text-[8.5px] uppercase text-white/50">{k.matchType}</span>
                            </span>
                          ))}
                        </div>
                      )}

                      {g.ads.map((ad, ai) => {
                        const issues = validateAd(ad);
                        return (
                          <div key={ai} className="space-y-2 rounded-lg border border-white/10 bg-white/[0.03] p-2.5">
                            <div className="flex items-center justify-between">
                              <span className="text-[9.5px] font-bold uppercase tracking-wider text-white/40">Ad {ai + 1} · RSA</span>
                              <span className="flex items-center gap-1.5">
                                {issues.length > 0 && <AlertTriangle size={12} style={{ color: ORANGE }} />}
                                <button type="button" onClick={() => deleteAd(gi, ai)} title="Remove this ad" className="text-white/30 hover:text-red-400"><Trash2 size={11} /></button>
                              </span>
                            </div>

                            {/* Google-style preview */}
                            <div className="rounded-lg bg-white p-3 text-[#202124]" style={{ fontFamily: "arial, sans-serif" }}>
                              <div className="mb-1 flex items-center gap-2 text-[11px]">
                                <span className="rounded-sm border border-[#202124] px-1 py-0.5 text-[9px] font-bold">Sponsored</span>
                                <span className="break-all text-[#202124]/70">{ad.url || "—"}</span>
                              </div>
                              <a className="text-[16px] leading-tight text-[#1a0dab] hover:underline" href="#">
                                {(ad.headlines[0] || "Headline") + " | " + (ad.headlines[1] || "Headline") + (ad.headlines[2] ? " | " + ad.headlines[2] : "")}
                              </a>
                              <div className="mt-0.5 break-all text-[11px] text-[#006621]">{ad.url}</div>
                              <p className="mt-1 text-[12px] leading-snug text-[#4d5156]">{ad.descriptions.join(" ")}</p>
                            </div>

                            {/* All headlines — over-limit flagged */}
                            <div>
                              <div className="mb-1 text-[9px] font-bold uppercase tracking-wider text-white/40">Headlines ({ad.headlines.length})</div>
                              <div className="flex flex-wrap gap-1">
                                {ad.headlines.map((h, hi) => (
                                  <span
                                    key={hi}
                                    className="rounded border px-1.5 py-0.5 text-[10.5px]"
                                    style={headlineTooLong(h)
                                      ? { borderColor: `${ORANGE}88`, background: `${ORANGE}1a`, color: ORANGE }
                                      : { borderColor: "rgba(255,255,255,0.1)", background: "rgba(255,255,255,0.04)", color: "rgba(255,255,255,0.85)" }}
                                  >
                                    {h}{headlineTooLong(h) && <span className="ml-1 font-bold">{h.length}/{HEADLINE_MAX}</span>}
                                  </span>
                                ))}
                              </div>
                            </div>

                            {/* All descriptions — over-limit flagged */}
                            <div>
                              <div className="mb-1 text-[9px] font-bold uppercase tracking-wider text-white/40">Descriptions ({ad.descriptions.length})</div>
                              <div className="space-y-1">
                                {ad.descriptions.map((d, di) => (
                                  <div
                                    key={di}
                                    className="rounded px-1.5 py-1 text-[10.5px]"
                                    style={descTooLong(d)
                                      ? { background: `${ORANGE}1a`, color: ORANGE }
                                      : { background: "rgba(255,255,255,0.04)", color: "rgba(255,255,255,0.8)" }}
                                  >
                                    {d}{descTooLong(d) && <span className="ml-1 font-bold">{d.length}/{DESC_MAX}</span>}
                                  </div>
                                ))}
                              </div>
                            </div>

                            {issues.length > 0 && (
                              <ul className="space-y-0.5 text-[10px]" style={{ color: ORANGE }}>
                                {issues.map((iss, ii) => <li key={ii}>⚠ {iss}</li>)}
                              </ul>
                            )}
                            {ad.impact && <div className="text-[10.5px]" style={{ color: GREEN }}>Impact: {ad.impact}</div>}
                          </div>
                        );
                      })}
                      {g.ads.length === 0 && <div className="text-[11px] text-white/40">No ads in this group (keywords only).</div>}

                      {/* PER-AD-GROUP SITELINKS — every URL is a VERIFIED real page from the site's inventory. */}
                      {(g.sitelinks.length > 0 || (g.droppedSitelinks || 0) > 0) && (
                        <div className="rounded-lg border border-white/10 bg-white/[0.03] p-2.5">
                          <div className="mb-1.5 text-[9px] font-bold uppercase tracking-wider text-white/40">Sitelinks ({g.sitelinks.length})</div>
                          <div className="space-y-1">
                            {g.sitelinks.map((s, si) => {
                              const tooLong = sitelinkTextTooLong(s.text);
                              return (
                                <div key={si} className="flex items-center justify-between gap-2 rounded bg-white/[0.03] px-2 py-1">
                                  <span className="flex shrink-0 items-center gap-1.5">
                                    <span className="rounded px-1 py-0.5 text-[8px] font-bold uppercase" style={s.kind === "main" ? { background: `${BLUE}22`, color: BLUE } : { background: `${BURNT}22`, color: BURNT_GLOW }}>{s.kind === "main" ? "Main" : "Specific"}</span>
                                    <span className="text-[11.5px] font-semibold" style={tooLong ? { color: ORANGE } : { color: "rgba(255,255,255,0.9)" }}>{s.text}{tooLong && <span className="ml-1 text-[9px] font-bold">{s.text.length}/{SITELINK_TEXT_MAX}</span>}</span>
                                  </span>
                                  <span className="flex min-w-0 items-center gap-1.5">
                                    <span className="min-w-0 break-all text-right font-mono text-[10px] text-[#4c8bf5]">{s.url || "—"}</span>
                                    <button type="button" onClick={(e) => { e.stopPropagation(); regenerateSitelinkAt(gi, s); }} title="Regenerate this sitelink" className="relative z-10 shrink-0 text-white/30 hover:text-white/80"><RefreshCw size={11} /></button>
                                    <button type="button" onClick={() => deleteSitelink(gi, si)} title="Remove this sitelink" className="shrink-0 text-white/30 hover:text-red-400"><X size={11} /></button>
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                          {(g.droppedSitelinks || 0) > 0 && (
                            <div className="mt-1.5 text-[10px] text-white/40">
                              {g.droppedSitelinks} sitelink{g.droppedSitelinks === 1 ? "" : "s"} skipped — no matching real page on the site.
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}

                <div
                  onDragOver={(e) => {
                    e.preventDefault();
                    setDragOver(true);
                  }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={(e) => {
                    e.preventDefault();
                    setDragOver(false);
                    if (e.dataTransfer.files?.length) handleImageFiles(e.dataTransfer.files);
                  }}
                  className={`rounded-xl border ${dragOver ? "border-white/40 bg-white/[0.06]" : "border-white/10 bg-white/[0.03]"} p-4 transition-colors`}
                >
                  <div className="mb-2 flex items-center justify-between">
                    <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-white/45">
                      Images {adImages.length > 0 && <span className="text-white/60">({adImages.length})</span>}
                    </div>
                    <div className="flex items-center gap-1.5">
                      <button
                        type="button"
                        onClick={generateAdImage}
                        disabled={generatingImage}
                        className="flex items-center gap-1 rounded-md px-2.5 py-1 text-[11px] font-semibold text-white transition-opacity disabled:opacity-60"
                        style={{ background: BURNT, boxShadow: `0 4px 14px ${BURNT}55` }}
                      >
                        {generatingImage ? (
                          <>
                            <Loader2 size={11} className="animate-spin" /> Generating…
                          </>
                        ) : (
                          <>
                            <Sparkles size={11} />
                            {generatedImageIdRef.current ? "Regenerate" : "Generate Image"}
                          </>
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={() => imageInputRef.current?.click()}
                        className="rounded-md border border-white/10 bg-white/[0.05] px-2.5 py-1 text-[11px] font-semibold text-white/85 hover:bg-white/[0.1]"
                      >
                        Upload
                      </button>
                    </div>
                    <input
                      ref={imageInputRef}
                      type="file"
                      accept="image/jpeg,image/png"
                      multiple
                      className="hidden"
                      onChange={(e) => {
                        if (e.target.files?.length) handleImageFiles(e.target.files);
                        e.target.value = "";
                      }}
                    />
                  </div>
                  {adImages.length === 0 && !generatingImage ? (
                    <div className="py-4 text-center text-[12px] text-white/55">
                      Drag & drop JPG or PNG, click Upload, or generate an AI image. Square or landscape recommended.
                    </div>
                  ) : (
                    <div className="grid grid-cols-4 gap-2">
                      {adImages.map((img) => (
                        <div key={img.id} className="group relative aspect-square overflow-hidden rounded-md border border-white/10 bg-black/30">
                          <img src={img.url} alt={img.name} className="h-full w-full object-cover" />
                          {img.generated && (
                            <span
                              className="absolute left-1 top-1 flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[9px] font-bold text-white"
                              style={{ background: BURNT }}
                            >
                              <Sparkles size={8} /> AI
                            </span>
                          )}
                          <button
                            type="button"
                            onClick={() => removeAdImage(img.id)}
                            className="absolute right-1 top-1 rounded-full bg-black/70 px-1.5 py-0.5 text-[10px] font-bold text-white opacity-0 transition-opacity group-hover:opacity-100"
                          >
                            ✕
                          </button>
                        </div>
                      ))}
                      {generatingImage && (
                        <div className="flex aspect-square items-center justify-center rounded-md border border-white/10 bg-black/30">
                          <Loader2 size={18} className="animate-spin text-white/70" />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ) : workspace.type === "image" ? (
              <div className="animate-fade-in space-y-3">
                {adImages.length === 0 && generatingImage ? (
                  <div className="flex aspect-square items-center justify-center rounded-xl border border-white/10 bg-black/30">
                    <div className="flex flex-col items-center gap-2 text-white/70">
                      <Loader2 size={28} className="animate-spin" />
                      <span className="text-[12px]">Generating image…</span>
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-2">
                    {adImages.map((img) => (
                      <div
                        key={img.id}
                        className="group relative aspect-square overflow-hidden rounded-xl border border-white/10 bg-black/30"
                      >
                        <img src={img.url} alt={img.name} className="h-full w-full object-cover" />
                        {img.generated && (
                          <span
                            className="absolute left-1.5 top-1.5 flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[9px] font-bold text-white"
                            style={{ background: BURNT }}
                          >
                            <Sparkles size={9} /> AI
                          </span>
                        )}
                        <button
                          type="button"
                          onClick={() => removeAdImage(img.id)}
                          className="absolute right-1.5 top-1.5 rounded-full bg-black/70 px-1.5 py-0.5 text-[10px] font-bold text-white opacity-0 transition-opacity group-hover:opacity-100"
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                    {generatingImage && (
                      <div className="flex aspect-square items-center justify-center rounded-xl border border-white/10 bg-black/30">
                        <Loader2 size={22} className="animate-spin text-white/70" />
                      </div>
                    )}
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => {
                    const prompt = (workspace.payload as any)?.prompt;
                    if (!prompt || generatingImage) return;
                    (async () => {
                      setGeneratingImage(true);
                      try {
                        const { data: imgData, error: imgErr } = await supabase.functions.invoke(
                          "dalle-generate",
                          {
                            body: {
                              prompt,
                              business: { domain: activeDomain },
                            },
                          },
                        );
                        if (imgErr) throw imgErr;
                        const url = imgData?.dataUrl;
                        if (!url) throw new Error("No image returned");
                        const newId = crypto.randomUUID();
                        setAdImages((prev) => {
                          const filtered = prev.filter((p) => p.id !== generatedImageIdRef.current);
                          return [...filtered, { id: newId, url, name: "AI generated", generated: true }];
                        });
                        generatedImageIdRef.current = newId;
                      } catch (err) {
                        console.error("Regenerate error:", err);
                        toast.error("Couldn't generate that image. Please try again.");
                      } finally {
                        setGeneratingImage(false);
                      }
                    })();
                  }}
                  disabled={generatingImage}
                  className="flex items-center gap-1.5 rounded-md px-3 py-1.5 text-[12px] font-semibold text-white transition-opacity disabled:opacity-60"
                  style={{ background: BURNT, boxShadow: `0 4px 14px ${BURNT}55` }}
                >
                  {generatingImage ? (
                    <>
                      <Loader2 size={12} className="animate-spin" /> Generating…
                    </>
                  ) : (
                    <>
                      <Sparkles size={12} /> Regenerate
                    </>
                  )}
                </button>
              </div>
            ) : workspace.type === "keywords" ? (
              <div className="animate-fade-in space-y-2">
                {workspace.payload.map((k: any) => (
                  <div
                    key={k.kw}
                    className="flex items-center justify-between rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3"
                  >
                    <div>
                      <div className="text-[13px] font-bold text-white">{k.kw}</div>
                      <div className="mt-0.5 text-[11px] text-white/50">
                        {k.match} · Vol {k.vol} · CPC {k.cpc}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 text-[11.5px] font-bold" style={{ color: GREEN }}>
                      <Activity size={11} />
                      {k.intent}/100
                    </div>
                  </div>
                ))}
              </div>
            ) : workspace.type === "budget" ? (
              <div className="space-y-2">
                {workspace.payload.map((b: any) => (
                  <div
                    key={b.name}
                    className="flex items-center justify-between rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3"
                  >
                    <div className="text-[13px] font-bold text-white">{b.name}</div>
                    <div className="flex items-center gap-3 text-[12px] text-white/70">
                      <span>${b.from}</span>
                      <span className="text-white/40">→</span>
                      <span className="font-bold text-white">${b.to}</span>
                      <span
                        className="rounded-md px-2 py-0.5 text-[11px] font-bold"
                        style={{
                          background: b.delta.startsWith("+") ? `${GREEN}22` : `${ORANGE}22`,
                          color: b.delta.startsWith("+") ? GREEN : ORANGE,
                        }}
                      >
                        {b.delta}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : workspace.type === "sitelinks" ? (
              <div className="animate-fade-in space-y-2">
                {workspace.payload.map((s: any, i: number) => (
                  <div key={i} className="rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3">
                    <div className="text-[13px] font-bold text-white">{s.title}</div>
                    {s.description && (
                      <div className="mt-0.5 text-[12px] text-white/70">{s.description}</div>
                    )}
                    {s.url && <div className="mt-1 text-[11px] text-white/45">{s.url}</div>}
                  </div>
                ))}
              </div>
            ) : (
              <div className="space-y-3">
                {workspace.payload.map((p: any) => (
                  <div key={p.label}>
                    <div className="mb-1 flex justify-between text-[12px] text-white/70">
                      <span className="font-semibold">{p.label}</span>
                      <span className="font-bold text-white">{p.pct}%</span>
                    </div>
                    <div className="h-2 overflow-hidden rounded-full bg-white/5">
                      <div
                        className="h-full rounded-full transition-all"
                        style={{ width: `${p.pct}%`, background: `linear-gradient(90deg, ${BURNT}, ${BURNT_GLOW})` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Advanced warning modal */}
      {showWarning && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/70 backdrop-blur-sm animate-fade-in">
          <div className="mx-4 w-full max-w-md rounded-2xl p-6" style={glass}>
            <div className="mb-4 flex items-center gap-3">
              <div
                className="flex h-10 w-10 items-center justify-center rounded-xl"
                style={{ background: `${BURNT}22`, border: `1px solid ${BURNT}55` }}
              >
                <AlertTriangle size={18} style={{ color: BURNT_GLOW }} />
              </div>
              <h3 className="text-[17px] font-bold text-white">Switch to Advanced Mode?</h3>
            </div>
            <p className="text-[13px] leading-relaxed text-white/65">
              Advanced mode unlocks AI-assisted workflows that can directly modify your campaigns.
              All changes still require your explicit approval.
            </p>
            <div className="mt-6 flex justify-end gap-2">
              <button
                onClick={() => setShowWarning(false)}
                className="rounded-lg border border-white/10 bg-white/[0.03] px-4 py-2 text-[12.5px] font-semibold text-white/80 hover:bg-white/[0.06]"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setShowWarning(false);
                  setMode("advanced");
                }}
                className="rounded-lg px-4 py-2 text-[12.5px] font-bold text-white"
                style={{ background: BURNT, boxShadow: `0 8px 24px ${BURNT}66` }}
              >
                Enable Advanced Mode
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AriaTab;
